"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { DatePickerWithRange } from "@/components/ui/datetime-picker-with-range";
import { Checkbox } from "@/components/ui/checkbox";
import { PizzaType } from "@/api/custom-schema";
import { getPhotoQualityCheckOptions } from "./photo-quality-checks";
import { useLocalization } from "@/context/localization-context";

interface PizzaFiltersProps {
  // Column visibility
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnVisibility: any;

  // Filter values
  idFilter: string;
  setIdFilter: (value: string) => void;
  nameFilter: string;
  setNameFilter: (value: string) => void;
  orderFilter: string;
  setOrderFilter: (value: string) => void;
  captureAtFilterStart?: Date;
  captureAtFilterEnd?: Date;
  setCaptureAtFilterStart: (value?: Date) => void;
  setCaptureAtFilterEnd: (value?: Date) => void;
  classificationFilter: string;
  setClassificationFilter: (value: string) => void;
  confidenceFilter: string;
  setConfidenceFilter: (value: string) => void;
  captureCountFilter: string;
  setCaptureCountFilter: (value: string) => void;
  componentCountFilter: string;
  setComponentCountFilter: (value: string) => void;
  donenessFilter: string;
  setDonenessFilter: (value: string) => void;
  overallEvaluationStatusFilter: string[];
  setOverallEvaluationStatusFilter: (value: string[]) => void;
  baseComponentsStatusFilter: string[];
  setBaseComponentsStatusFilter: (value: string[]) => void;
  extrasStatusFilter: string[];
  setExtrasStatusFilter: (value: string[]) => void;
  removalsStatusFilter: string[];
  setRemovalsStatusFilter: (value: string[]) => void;
  pizzaSaverStatusFilter: string[];
  setPizzaSaverStatusFilter: (value: string[]) => void;
  pizzaTypeFilter: string[];
  setPizzaTypeFilter: (value: string[]) => void;
  pizzaTypes?: PizzaType[];
  hasExtrasFilter: boolean;
  setHasExtrasFilter: (value: boolean) => void;
  hasRemovalsFilter: boolean;
  setHasRemovalsFilter: (value: boolean) => void;
  photoQualityStatusFilter: string[];
  setPhotoQualityStatusFilter: (value: string[]) => void;
  photoQualityCheckFilter: string[];
  setPhotoQualityCheckFilter: (value: string[]) => void;
  componentErrorFilter: string;
  setComponentErrorFilter: (value: string) => void;
}

export function PizzaFilters({
  columnVisibility,
  idFilter,
  setIdFilter,
  nameFilter,
  setNameFilter,
  orderFilter,
  setOrderFilter,
  captureAtFilterStart,
  captureAtFilterEnd,
  setCaptureAtFilterStart,
  setCaptureAtFilterEnd,
  classificationFilter,
  setClassificationFilter,
  confidenceFilter,
  setConfidenceFilter,
  captureCountFilter,
  setCaptureCountFilter,
  componentCountFilter,
  setComponentCountFilter,
  donenessFilter,
  setDonenessFilter,
  overallEvaluationStatusFilter,
  setOverallEvaluationStatusFilter,
  baseComponentsStatusFilter,
  setBaseComponentsStatusFilter,
  extrasStatusFilter,
  setExtrasStatusFilter,
  removalsStatusFilter,
  setRemovalsStatusFilter,
  pizzaSaverStatusFilter,
  setPizzaSaverStatusFilter,
  pizzaTypeFilter,
  setPizzaTypeFilter,
  pizzaTypes,
  hasExtrasFilter,
  setHasExtrasFilter,
  hasRemovalsFilter,
  setHasRemovalsFilter,
  photoQualityStatusFilter,
  setPhotoQualityStatusFilter,
  photoQualityCheckFilter,
  setPhotoQualityCheckFilter,
  componentErrorFilter,
  setComponentErrorFilter,
}: PizzaFiltersProps) {
  const { t } = useLocalization();
  const evaluationOptions = [
    { label: t("status.ok"), value: "OK" },
    { label: t("status.nok"), value: "NOK" },
    { label: t("pizzas.filters.statusNone"), value: "NONE" },
  ];

  return (
    <>
      {columnVisibility.id && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="pizza-ids">{t("pizzas.filters.pizzaIds")}</Label>
          <Input
            id="pizza-ids"
            placeholder={t("pizzas.filters.pizzaIdsPlaceholder")}
            value={idFilter}
            onChange={(event) => {
              const value = event.target.value;
              setIdFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility.name && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="pizza-name">{t("pizzas.filters.pizzaName")}</Label>
          <Input
            id="pizza-name"
            placeholder={t("pizzas.filters.pizzaNamePlaceholder")}
            value={nameFilter}
            onChange={(event) => {
              const value = event.target.value;
              setNameFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility.order_id && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="order-ids">{t("pizzas.filters.orderIds")}</Label>
          <Input
            id="order-ids"
            placeholder={t("pizzas.filters.orderIdsPlaceholder")}
            value={orderFilter}
            onChange={(event) => {
              const value = event.target.value;
              setOrderFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility.capture_at && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.captureDateRange")}</Label>
          <DatePickerWithRange
            className="w-full"
            value={
              captureAtFilterStart || captureAtFilterEnd
                ? ({
                    from: captureAtFilterStart
                      ? new Date(captureAtFilterStart)
                      : undefined,
                    to: captureAtFilterEnd
                      ? new Date(captureAtFilterEnd)
                      : undefined,
                  })
                : undefined
            }
            onChange={(range) => {
              if (range && "from" in range && range.from) {
                setCaptureAtFilterStart(range.from);
              } else {
                setCaptureAtFilterStart(undefined);
              }
              if (range && "to" in range && range.to) {
                const endOfDay = new Date(range.to);
                endOfDay.setHours(23, 59, 59, 999);
                range.to = endOfDay;
                setCaptureAtFilterEnd(endOfDay);
              } else {
                setCaptureAtFilterEnd(undefined);
              }
            }}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.pizza_detection"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.detection")}</Label>
          <MultiSelect
            options={[
              { label: t("common.pizza"), value: "ok pizza" },
              { label: t("status.noPizza"), value: "no pizza" },
            ]}
            selected={
              classificationFilter ? [classificationFilter.toLowerCase()] : []
            }
            onChange={(values) => {
              setClassificationFilter(values.length > 0 ? values[0] : "");
            }}
            placeholder={t("pizzas.filters.detectionPlaceholder")}
            enableSearch={false}
            label={t("pizzas.filters.detection")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.confidence"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="confidence">{t("pizzas.filters.confidenceRange")}</Label>
          <Input
            id="confidence"
            placeholder={t("pizzas.filters.confidencePlaceholder")}
            value={confidenceFilter}
            onChange={(event) => {
              const value = event.target.value;
              setConfidenceFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility.capture_count && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="capture-count">{t("pizzas.filters.captureCount")}</Label>
          <Input
            id="capture-count"
            placeholder={t("pizzas.filters.captureCountPlaceholder")}
            value={captureCountFilter}
            onChange={(event) => {
              const value = event.target.value;
              setCaptureCountFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.component_count"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="component-count">{t("pizzas.columns.componentCount")}</Label>
          <Input
            id="component-count"
            placeholder={t("pizzas.filters.componentCountPlaceholder")}
            value={componentCountFilter}
            onChange={(event) => {
              const value = event.target.value;
              setComponentCountFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.doneness_evaluation"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="doneness">{t("pizzas.columns.doneness")}</Label>
          <Input
            id="doneness"
            placeholder={t("pizzas.filters.donenessPlaceholder")}
            value={donenessFilter}
            onChange={(event) => {
              const value = event.target.value;
              setDonenessFilter(value.trim());
            }}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.overall_evaluation"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.columns.verdict")}</Label>
          <MultiSelect
            options={evaluationOptions}
            selected={overallEvaluationStatusFilter}
            onChange={setOverallEvaluationStatusFilter}
            placeholder={t("pizzas.filters.verdictPlaceholder")}
            enableSearch={false}
            label={t("pizzas.columns.verdict")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility[
        "evaluation.metadata.components_evaluation.base_components"
      ] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.ingredientsStatus")}</Label>
          <MultiSelect
            options={evaluationOptions}
            selected={baseComponentsStatusFilter}
            onChange={setBaseComponentsStatusFilter}
            placeholder={t("pizzas.filters.ingredientsStatusPlaceholder")}
            enableSearch={false}
            label={t("pizzas.columns.ingredients")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.components_evaluation.extras"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.extrasStatus")}</Label>
          <MultiSelect
            options={evaluationOptions}
            selected={extrasStatusFilter}
            onChange={setExtrasStatusFilter}
            placeholder={t("pizzas.filters.extrasStatusPlaceholder")}
            enableSearch={false}
            label={t("pizzas.columns.extras")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility[
        "evaluation.metadata.components_evaluation.removals"
      ] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.removalsStatus")}</Label>
          <MultiSelect
            options={evaluationOptions}
            selected={removalsStatusFilter}
            onChange={setRemovalsStatusFilter}
            placeholder={t("pizzas.filters.removalsStatusPlaceholder")}
            enableSearch={false}
            label={t("pizzas.columns.removals")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.pizza_saver_evaluation"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.pizzaSaverStatus")}</Label>
          <MultiSelect
            options={evaluationOptions}
            selected={pizzaSaverStatusFilter}
            onChange={setPizzaSaverStatusFilter}
            placeholder={t("pizzas.filters.pizzaSaverStatusPlaceholder")}
            enableSearch={false}
            label={t("pizzas.columns.pizzaSaver")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.photo_quality_evaluation"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.photoQualityStatus")}</Label>
          <MultiSelect
            options={evaluationOptions}
            selected={photoQualityStatusFilter}
            onChange={setPhotoQualityStatusFilter}
            placeholder={t("pizzas.filters.photoQualityStatusPlaceholder")}
            enableSearch={false}
            label={t("pizzas.columns.photoQuality")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility["evaluation.metadata.photo_quality_evaluation"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.photoQualityCheck")}</Label>
          <MultiSelect
            options={getPhotoQualityCheckOptions()}
            selected={photoQualityCheckFilter}
            onChange={setPhotoQualityCheckFilter}
            placeholder={t("pizzas.filters.photoQualityCheckPlaceholder")}
            enableSearch={false}
            label={t("pizzas.filters.photoQualityCheck")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      {columnVisibility["pizza_type"] && (
        <div className="flex flex-col gap-2 min-w-0">
          <Label>{t("pizzas.filters.pizzaTypes")}</Label>
          <MultiSelect
            options={
              pizzaTypes?.map((type) => ({
                label: type.name,
                value: type.id,
              })) || []
            }
            selected={pizzaTypeFilter}
            onChange={setPizzaTypeFilter}
            placeholder={t("pizzas.filters.pizzaTypesPlaceholder")}
            enableSearch={true}
            searchPlaceholder={t("pizzas.filters.pizzaTypesSearch")}
            label={t("pizzas.filters.availablePizzaTypes")}
            showClearAll={true}
            showSelectAll={false}
          />
        </div>
      )}
      <div className="flex flex-col gap-2 min-w-0">
        <Label htmlFor="component-error">{t("pizzas.filters.componentError")}</Label>
        <Input
          id="component-error"
          placeholder={t("pizzas.filters.componentErrorPlaceholder")}
          value={componentErrorFilter}
          onChange={(event) => setComponentErrorFilter(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-3 min-w-0">
        <Label>{t("pizzas.filters.extrasAndRemovals")}</Label>
        <div className="flex items-center space-x-2 mx-2">
          <Checkbox
            id="has-extras"
            checked={hasExtrasFilter}
            onCheckedChange={(checked) => setHasExtrasFilter(checked === true)}
          />
          <Label
            htmlFor="has-extras"
            className="text-sm font-normal cursor-pointer"
          >
            {t("pizzas.filters.hasExtras")}
          </Label>
        </div>
        <div className="flex items-center space-x-2 mx-2">
          <Checkbox
            id="has-removals"
            checked={hasRemovalsFilter}
            onCheckedChange={(checked) =>
              setHasRemovalsFilter(checked === true)
            }
          />
          <Label
            htmlFor="has-removals"
            className="text-sm font-normal cursor-pointer"
          >
            {t("pizzas.filters.hasRemovals")}
          </Label>
        </div>
      </div>
    </>
  );
}
